import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Chart from "react-apexcharts";
import { ApexOptions } from 'apexcharts';
import LoadingData from './LoadingData';
import { Box, Select, MenuItem, FormControl } from '@mui/material';
import { createCategoryChartConfig, createYAxisConfig, getHistogramColors } from './shared/ChartConfig';

interface HistogramDataItem {
  time_of_day: string;
  consumption: number;
  gCO_2: number;
  cost?: number;
}

interface PercentileData {
  first_25_percent: HistogramDataItem[];
  first_50_percent: HistogramDataItem[];
  first_75_percent: HistogramDataItem[];
  remaining_25_percent: HistogramDataItem[];
}

export type HistogramDisplayMode = 'consumption' | 'cost';

const NEGATIVE_COST_COLOR = '#6666ff';

interface HistogramProps { 
  dateFrom: string;
  dateTo: string;
  consumptionType: number;
  aggregation: string;
  loading: boolean;
  percentileData: PercentileData | null;
  histType: string;
  isFreeUser: boolean;
  days: string;
  onDaysChange: (days: string) => void;
  displayMode?: HistogramDisplayMode;
  onDisplayModeChange?: (mode: HistogramDisplayMode) => void;
}

const Histogram: React.FC<HistogramProps> = ({ 
  dateFrom, 
  dateTo, 
  consumptionType, 
  aggregation, 
  loading,
  percentileData,
  histType,
  isFreeUser,
  days,
  onDaysChange,
  displayMode = 'consumption',
  onDisplayModeChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [xData, setXData] = React.useState<Date[]>([]);

  // Process percentile data to create x-axis data
  React.useEffect(() => {
    if (percentileData && Object.keys(percentileData).length > 0) {
      // Create a unified set of time periods from all percentile data
      const allTimePeriods = new Set<string>();
      
      // Add all time periods from all percentile arrays
      Object.values(percentileData).forEach((percentileArray: HistogramDataItem[]) => {
        percentileArray.forEach((item: HistogramDataItem) => allTimePeriods.add(item.time_of_day));
      });
      
      // Convert to sorted array of timestamps
      const timestamps = Array.from(allTimePeriods).map(timeOfDay => {
        const timeParts = timeOfDay.split(':');
        const hours = parseInt(timeParts[0]);
        const minutes = parseInt(timeParts[1]);
        const seconds = parseInt(timeParts[2]);
        
        const baseDate = new Date(dateFrom);
        const timestamp = new Date(baseDate);
        timestamp.setHours(hours, minutes, seconds, 0);
        
        return timestamp;
      });
      
      // Sort timestamps chronologically
      timestamps.sort((a, b) => a.getTime() - b.getTime());
      setXData(timestamps);
    } else {
      setXData([]);
    }
  }, [percentileData, dateFrom]);

  const showCostDisplay = histType === 'consumption' && displayMode === 'cost';

  const getItemValue = (item: HistogramDataItem) => {
    if (histType === 'co2') return item.gCO_2;
    if (showCostDisplay) return item.cost ?? 0;
    return item.consumption;
  };

  // Determine if we should use kg or g based on maximum value
  const getMaxValue = () => {
    if (!percentileData) return 0;
    
    let maxValue = 0;
    Object.values(percentileData).forEach((percentileArray: HistogramDataItem[]) => {
      percentileArray.forEach((item: HistogramDataItem) => {
        maxValue = Math.max(maxValue, getItemValue(item));
      });
    });
    return maxValue;
  };

  const maxValue = getMaxValue();
  const useKg = histType === 'co2' && maxValue > 1000;
  const unit = useKg ? 'kg' : 'g';
  const conversionFactor = useKg ? 1000 : 1;

  const getMinValue = () => {
    if (!percentileData || !showCostDisplay) return 0;

    let minValue = 0;
    Object.values(percentileData).forEach((percentileArray: HistogramDataItem[]) => {
      percentileArray.forEach((item: HistogramDataItem) => {
        minValue = Math.min(minValue, item.cost ?? 0);
      });
    });
    return minValue;
  };

  const minValue = getMinValue();
  const yAxisMin = showCostDisplay
    ? (minValue < 0 ? minValue * 1.1 : 0)
    : 0;

  const getYAxisLabel = () => {
    if (histType === 'co2') return `CO₂ Emissions (${unit})`;
    if (showCostDisplay) return 'Cost (£)';
    return 'Consumption (kWh)';
  };

  const formatTooltipValue = (val: number) => {
    if (histType === 'co2') return `${val.toFixed(1)} ${unit} CO₂`;
    if (showCostDisplay) return `£${val.toFixed(2)}`;
    return `${val.toFixed(1)} kWh`;
  };

  // Theme-aware colors for percentile visualization
  const chartColors = getHistogramColors(theme);

  // Helper function to create data series for a percentile
  const createPercentileSeries = React.useCallback((percentileArray: HistogramDataItem[], seriesName: string, color: string) => {
    if (!percentileArray || percentileArray.length === 0) return null;
    
    const getValue = (item: HistogramDataItem) => {
      if (histType === 'co2') return item.gCO_2;
      if (showCostDisplay) return item.cost ?? 0;
      return item.consumption;
    };

    // Create a map for quick lookup
    const dataMap = new Map(percentileArray.map(item => [item.time_of_day, getValue(item)]));
    
    // Return data aligned with x-axis categories — use null (not 0) for empty slots
    // so stacked negative bars render (zeros in sibling series block negatives)
    const data = xData.map(timestamp => {
      const timeOfDay = timestamp.toTimeString().split(' ')[0];
      const rawValue = dataMap.get(timeOfDay);
      if (rawValue === undefined) {
        return null;
      }
      return showCostDisplay ? rawValue : rawValue / conversionFactor;
    });
    
    return {
      name: seriesName,
      data,
      color: color
    };
  }, [histType, showCostDisplay, xData, conversionFactor]);

  // Create series for each percentile
  const chartSeries = React.useMemo(() => {
    if (!percentileData) return [];
    
    const series = [];
    
    // First 25% (highest emitters) - Always Red
    const first25Series = createPercentileSeries(percentileData.first_25_percent, 'Highest', chartColors.first25);
    if (first25Series) series.push(first25Series);
    
    // 25-50% (second highest) - Always Orange
    const first50Series = createPercentileSeries(percentileData.first_50_percent, 'High', chartColors.first50);
    if (first50Series) series.push(first50Series);
    
    // 50-75% (medium) - Always Yellow
    const first75Series = createPercentileSeries(percentileData.first_75_percent, 'Moderate', chartColors.first75);
    if (first75Series) series.push(first75Series);
    
    // Remaining 25% (lowest) - Always Green
    const remainingSeries = createPercentileSeries(percentileData.remaining_25_percent, 'Lowest', chartColors.remaining);
    if (remainingSeries) series.push(remainingSeries);
    
    return series;
  }, [percentileData, chartColors, createPercentileSeries]);

  const getSuffix = (day: number) => {
    return (day % 10 === 1 && day !== 11) ? 'st' : 
      (day % 10 === 2 && day !== 12) ? 'nd' : 
      (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  }

  const formatDayTitle = (from: string, to: string) => {
    const dateFrom = new Date(from);
    const dateTo = new Date(to);
    const dayFrom = `${dateFrom.getDate()}`;
    const suffix = getSuffix(dateFrom.getDate())
    const monthFrom = isMobile 
      ? dateFrom.toLocaleString('default', { month: 'short' })
      : dateFrom.toLocaleString('default', { month: 'long' });
    const yearFrom = `${dateFrom.getFullYear()}`;
    const dayTo = `${dateTo.getDate()}`;        
    const suffixTo = getSuffix(dateTo.getDate())
    const monthTo = isMobile 
      ? dateTo.toLocaleString('default', { month: 'short' })
      : dateTo.toLocaleString('default', { month: 'long' });
    const yearTo = `${dateTo.getFullYear()}`;

    switch (aggregation) {
      case 'none':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'hour':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'month':
        if (monthFrom === monthTo) {
          return `${monthFrom} ${yearTo}`;
        } else {          
          if (yearFrom === yearTo) {
            return `${monthFrom} - ${monthTo} ${yearTo}`;
          } else {
            return `${monthFrom} ${yearFrom} - ${monthTo} ${yearTo}`;
          }
        }
      case 'year':
        if (yearFrom === yearTo) {
          return `${yearTo}`;
        } else {          
          return `${yearFrom} - ${yearTo}`;
        }
      default:        
        if (yearFrom === yearTo) {
          if (dayFrom === dayTo) {
            return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
          }
          return `${dayFrom}${suffix} ${monthFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        } else {
          return `${dayFrom}${suffix} ${monthFrom} ${yearFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        }
    }
  };

  const baseChartConfig = createCategoryChartConfig({
      theme,
      chartType: 'bar',
      height: 350,
      categories: xData,
      tickAmount: 6,
      tickPlacement: 'between',
      labelFormatter: (value: string) => {
        const date = new Date(value);
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      },
      chartId: 'histogram',
      stacked: true,
      isMobile,
      foreColor: chartColors.text
    });

  const chartOptions = {
    ...baseChartConfig,
    chart: {
      ...baseChartConfig.chart,
      ...(showCostDisplay && minValue < 0 ? { stackType: 'normal' as const } : {}),
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        borderRadius: showCostDisplay && minValue < 0 ? 0 : 3,
        borderRadiusApplication: 'end' as const,
        ...(showCostDisplay && minValue < 0
          ? {}
          : { borderRadiusWhenStacked: 'all' as const }),
        ...(showCostDisplay && minValue < 0 && {
          colors: {
            ranges: [{
              from: -Number.MAX_VALUE,
              to: 0,
              color: NEGATIVE_COST_COLOR
            }]
          }
        })
      },
    },
    stroke: {
      show: true,
      width: 0,
      colors: ['transparent']
    },
    yaxis: {
      ...createYAxisConfig(theme, getYAxisLabel(), {
        min: yAxisMin,
        decimalsInFloat: showCostDisplay ? 2 : 1
      }),
      title: {
        ...createYAxisConfig(theme, getYAxisLabel(), {
          min: yAxisMin,
          decimalsInFloat: showCostDisplay ? 2 : 1
        }).title,
        style: {
          fontSize: isMobile ? '13px' : '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: isMobile ? 500 : 700,
          color: theme.palette.text.primary
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      enabled: true,
      theme: theme.palette.mode,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif'
      },
      y: {
        formatter: function (val: number) {
          if (val === null || val === undefined) return '';
          return formatTooltipValue(val);
        }
      }
    },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const,
      fontSize: isMobile ? '12px' : '16px',
      fontFamily: 'Inter, sans-serif',
      markers: {
        radius: 3,
        shape: 'circle' as const
      },
      labels: {
        colors: chartColors.text
      }
    },
    // Mobile optimizations - only apply on mobile
    ...(isMobile && {
      xaxis: {
        ...createCategoryChartConfig({
          theme,
          chartType: 'bar',
          height: 350,
          categories: xData,
          tickAmount: 6,
          tickPlacement: 'between',
          labelFormatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
          },
          chartId: 'histogram',
          stacked: true,
          isMobile,
          foreColor: chartColors.text
        }).xaxis,
        tickAmount: 3
      }
    })
  };

  return (
    <Card sx={{ width: '100%', position: 'relative', backgroundColor: theme.palette.background.paper, px: { xs: 1, md: 0 } }} data-chart-id={`histogram-${consumptionType === 1 ? 'electricity' : 'gas'}`}>
      <CardContent>
        <LoadingData open={loading} />        
        <Stack sx={{ 
          justifyContent: 'space-between', 
          flexDirection: { xs: 'column', md: 'row' }, 
          alignItems: { xs: 'stretch', md: 'flex-start' }, 
          mb: 1 
        }}>
          <Box>
            <Typography variant="h2">
              {histType === 'co2'
                ? 'Total by Time of Day'
                : showCostDisplay
                  ? 'Cost by Time of Day'
                  : 'Consumption by Time of Day'}
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary' }}>
              {formatDayTitle(dateFrom, dateTo)}
            </Typography>
          </Box>
          {!isFreeUser && (
            <Stack 
              direction="row" 
              spacing={2} 
              sx={{ 
                mt: 1,
                alignSelf: { xs: 'flex-end', md: 'auto' },
              }}
            >
              {histType === 'consumption' && onDisplayModeChange && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  {(!isMobile) && (
                    <Typography variant="h6" gutterBottom>
                      Display
                    </Typography>
                  )}
                  <Select
                    sx={{ borderRadius: '8px !important' }}
                    labelId="display-select-label"
                    id="display-select"
                    value={displayMode}
                    onChange={(e) => onDisplayModeChange(e.target.value as HistogramDisplayMode)}
                  >
                    <MenuItem value="consumption">Consumption</MenuItem>
                    <MenuItem value="cost">Cost</MenuItem>
                  </Select>
                </FormControl>
              )}
              {aggregation !== 'none' && aggregation !== 'hour' && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  {(!isMobile) && (
                    <Typography variant="h6" gutterBottom>
                      Days of week
                    </Typography>
                  )}
                  <Select                  
                    sx={{ borderRadius: '8px !important' }}
                    labelId="days-select-label"
                    id="days-select"
                    value={days}
                    label="Days of week"
                    onChange={(e) => onDaysChange(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="weekdays">Weekdays</MenuItem>
                    <MenuItem value="weekends">Weekends</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}
        </Stack>
        <Box sx={{ position: 'relative', ml: { xs: '-5px', md: 0 }, mr: { xs: '-20px', md: 0 }, mb: { xs: '-10px', md: 0 } }}>
          <Chart
            options={chartOptions}
            series={chartSeries as ApexOptions['series']}
            type="bar"
            height={isMobile ? Math.min(250, window.innerWidth * 0.8) : 350}
          />
          
          {/* No data overlay */}
          {!loading && chartSeries.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.surface,
                zIndex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                No data
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Histogram;
